import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { KeyType } from '../types';
import { keyTypesApi } from '../services/api';

interface KeyTypesState {
  keyTypes: KeyType[];
  loading: boolean;
  error: string | null;
}

const initialState: KeyTypesState = {
  keyTypes: [],
  loading: false,
  error: null,
};

// Async thunks for API operations
export const fetchKeyTypes = createAsyncThunk(
  'keyTypes/fetchKeyTypes',
  async (_, { rejectWithValue }) => {
    try {
      return await keyTypesApi.getAll();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const createKeyTypeAsync = createAsyncThunk(
  'keyTypes/createKeyType',
  async (keyType: KeyType, { rejectWithValue }) => {
    try {
      return await keyTypesApi.create(keyType);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const updateKeyTypeAsync = createAsyncThunk(
  'keyTypes/updateKeyType',
  async (keyType: KeyType, { rejectWithValue }) => {
    try {
      return await keyTypesApi.update(keyType);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const deleteKeyTypeAsync = createAsyncThunk(
  'keyTypes/deleteKeyType',
  async (id: string, { rejectWithValue }) => {
    try {
      await keyTypesApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const keyTypesSlice = createSlice({
  name: 'keyTypes',
  initialState,
  reducers: {
    setKeyTypes: (state, action: PayloadAction<KeyType[]>) => {
      state.keyTypes = action.payload;
    },
    addKeyType: (state, action: PayloadAction<KeyType>) => {
      state.keyTypes.push(action.payload);
    },
    updateKeyType: (state, action: PayloadAction<KeyType>) => {
      const index = state.keyTypes.findIndex(kt => kt.id === action.payload.id);
      if (index !== -1) {
        state.keyTypes[index] = action.payload;
      }
    },
    removeKeyType: (state, action: PayloadAction<string>) => {
      state.keyTypes = state.keyTypes.filter(kt => kt.id !== action.payload);
    },
    clearKeyTypeError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch key types
      .addCase(fetchKeyTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKeyTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.keyTypes = action.payload;
      })
      .addCase(fetchKeyTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create key type
      .addCase(createKeyTypeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createKeyTypeAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.keyTypes.push(action.payload);
      })
      .addCase(createKeyTypeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update key type
      .addCase(updateKeyTypeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateKeyTypeAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.keyTypes.findIndex(kt => kt.id === action.payload.id);
        if (index !== -1) {
          state.keyTypes[index] = action.payload;
        }
      })
      .addCase(updateKeyTypeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete key type
      .addCase(deleteKeyTypeAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteKeyTypeAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.keyTypes = state.keyTypes.filter(kt => kt.id !== action.payload);
      })
      .addCase(deleteKeyTypeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setKeyTypes, addKeyType, updateKeyType, removeKeyType, clearKeyTypeError } = keyTypesSlice.actions;
export default keyTypesSlice.reducer;
